import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { getTranslation } from '../translations';
import Footer from '../components/Footer';
import LoadingSkeleton from '../components/LoadingSkeleton';
import ShareStoryModal from '../components/ShareStoryModal';
import FolderSidebar from '../components/FolderSidebar';
import BulkActionToolbar from '../components/BulkActionToolbar';
import './Dashboard.css';

export default function Dashboard({ user, logout, updateUser, refreshUser }) {
  const t = (key) => getTranslation(user?.language || 'en', key);
  const isGerman = user?.language === 'de';
  const navigate = useNavigate();
  const location = useLocation();
  const [stories, setStories] = useState([]);
  const [generatingStories, setGeneratingStories] = useState([]); // Stories currently being generated
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [weeklyLimit, setWeeklyLimit] = useState(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total_pages: 1,
    has_more: false
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedStoryToShare, setSelectedStoryToShare] = useState(null);
  
  // New state for folder management
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null); // null = "All Stories", "favorites" = Favorites
  const [selectedStories, setSelectedStories] = useState([]); // Array of story IDs
  const [selectionMode, setSelectionMode] = useState(false);
  const storyCacheKey = user?.id ? `dashboardStories:${user.id}` : null;

  const readCachedStories = () => {
    if (!storyCacheKey) return [];

    try {
      const rawCache = localStorage.getItem(storyCacheKey);
      if (!rawCache) return [];
      const parsedCache = JSON.parse(rawCache);
      return Array.isArray(parsedCache) ? parsedCache : [];
    } catch (error) {
      console.error('Failed to read cached dashboard stories:', error);
      return [];
    }
  };

  const writeCachedStories = (nextStories) => {
    if (!storyCacheKey) return;

    try {
      localStorage.setItem(storyCacheKey, JSON.stringify(nextStories.slice(0, 40)));
    } catch (error) {
      console.error('Failed to cache dashboard stories:', error);
    }
  };

  // Helper function to truncate long themes for display
  const getTruncatedTheme = (story) => {
    const theme = story.theme_description || story.theme;
    const maxLength = 80; // Max characters to display
    if (theme && theme.length > maxLength) {
      return theme.substring(0, maxLength) + '...';
    }
    return theme;
  };

  useEffect(() => {
    const cachedStories = readCachedStories();
    if (cachedStories.length > 0) {
      setStories(cachedStories);
      setLoading(false);
    }
  }, [storyCacheKey]);

  useEffect(() => {
    // Check if there's a generating story from navigation state
    if (location.state?.generatingStory) {
      const genStory = location.state.generatingStory;
      setLoading(false);
      
      // Check if this generating story is already added (prevent duplicates)
      setGeneratingStories(prev => {
        const alreadyExists = prev.some(s => s.id === genStory.id);
        if (alreadyExists) {
          return prev;
        }
        return [...prev, genStory];
      });
      
      // Clear navigation state to prevent re-adding on refresh
      window.history.replaceState({}, document.title);
      
      // Poll for the actual story to appear quickly so the dashboard updates while generation runs.
      let pollCount = 0;
      const maxPolls = 90; // 3 minutes / 2 seconds
      const pollInterval = setInterval(async () => {
        pollCount++;
        
        try {
          // Fetch stories to check if the new story appeared (silent to avoid loading flicker)
          await fetchStories(1, false, true);
          
          // Stop polling after max attempts
          if (pollCount >= maxPolls) {
            clearInterval(pollInterval);
            // Remove generating story even if not found
            setGeneratingStories(prev => prev.filter(s => s.id !== genStory.id));
          }
        } catch (error) {
          console.error('Error polling for story:', error);
        }
      }, 2000);
      
      // Store interval ID to clean up if component unmounts
      return () => clearInterval(pollInterval);
    }
  }, [location.state?.generatingStory?.id]);

  useEffect(() => {
    // Always refresh user data on Dashboard mount to ensure we have latest subscription info
    const refreshData = async () => {
      const tasks = [
        fetchStories(1, true),
        fetchWeeklyLimit(),
        fetchSubscriptionInfo()
      ];
      if (refreshUser) {
        tasks.push(refreshUser());
      }
      await Promise.all(tasks);
      checkCheckoutSuccess();
    };
    
    refreshData();
    fetchFolders(); // Fetch folders on mount
    
    // Auto-refresh stories every 60 seconds (reduced from 15 for better performance)
    const refreshInterval = setInterval(() => {
      fetchStories(1, true, true); // Refresh first page only, silently (no loading state)
    }, 60000); // 60 seconds
    
    // Cleanup interval on unmount
    return () => clearInterval(refreshInterval);
  }, []);

  // Refresh limits when user subscription changes
  useEffect(() => {
    if (user) {
      fetchWeeklyLimit();
      fetchSubscriptionInfo();
    }
  }, [user?.subscription_tier]);

  const checkCheckoutSuccess = async () => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const paymentMethod = params.get('payment_method');
    const purchaseType = params.get('type');
    const subscriptionId = params.get('subscription_id'); // PayPal subscription ID
    const token_param = params.get('token'); // PayPal token
    const ba_token = params.get('ba_token'); // PayPal BA token
    const storyPurchaseSuccess = params.get('story_purchase_success');
    
    // Handle Single Story Purchase Success (new implementation)
    if (storyPurchaseSuccess === 'true') {
      // PayPal flow
      if (paymentMethod === 'paypal' && token_param) {
        try {
          const token = localStorage.getItem('token');
          // Extract order_id from token parameter (PayPal returns it)
          const orderId = token_param;
          
          // Capture PayPal payment
          const response = await axios.post(
            `${API}/purchase-story/paypal-capture/${orderId}`,
            {},
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );
          
          if (response.data.payment_status === 'paid') {
            toast.success(isGerman ? '✨ Geschichts-Guthaben hinzugefügt!' : '✨ Story credit added!');
            if (refreshUser) await refreshUser();
            await fetchWeeklyLimit();
          }
        } catch (error) {
          console.error('Error capturing PayPal payment:', error);
          toast.error(isGerman ? 'Fehler beim Überprüfen der Zahlung' : 'Error processing payment');
        }
        window.history.replaceState({}, '', '/dashboard');
        return;
      }
      
      // Stripe flow - verify payment and add credit
      if (paymentMethod === 'stripe' && sessionId) {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.post(
            `${API}/purchase-story/verify-payment?session_id=${sessionId}`,
            {},
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );
          
          if (response.data.status === 'success' || response.data.status === 'already_processed' || response.data.status === 'already_added') {
            toast.success(isGerman ? '✨ Geschichts-Guthaben hinzugefügt!' : '✨ Story credit added!');
            if (refreshUser) await refreshUser();
            await fetchWeeklyLimit();
          }
        } catch (error) {
          console.error('Error verifying Stripe payment:', error);
          toast.error(isGerman ? 'Fehler beim Überprüfen der Zahlung' : 'Error verifying payment');
        }
        window.history.replaceState({}, '', '/dashboard');
        return;
      }
      
      // Fallback if no specific payment method
      toast.success(isGerman ? '✨ Zahlung erfolgreich!' : '✨ Payment successful!');
      if (refreshUser) await refreshUser();
      await fetchWeeklyLimit();
      window.history.replaceState({}, '', '/dashboard');
      return;
    }
    
    // Handle Single Story Purchase - Stripe (old implementation - keep for backward compatibility)
    if (purchaseType === 'single_story' && sessionId) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API}/subscription/single-story-status-stripe`,
          {
            params: { session_id: sessionId },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        if (response.data.status === 'success') {
          toast.success(isGerman ? '✨ Geschichts-Guthaben hinzugefügt!' : '✨ Story credit added!');
          if (refreshUser) await refreshUser();
          await fetchWeeklyLimit();
        }
      } catch (error) {
        console.error('Error checking single story status:', error);
        toast.error(isGerman ? 'Fehler beim Überprüfen der Zahlung' : 'Error checking payment');
      }
      window.history.replaceState({}, '', '/dashboard');
      return;
    }
    
    // Handle Single Story Purchase - PayPal (old implementation - keep for backward compatibility)
    if (purchaseType === 'single_story_paypal' && token_param) {
      try{
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API}/subscription/single-story-status-paypal`,
          {
            params: { token: token_param },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        if (response.data.status === 'success') {
          toast.success(isGerman ? '✨ Geschichts-Guthaben hinzugefügt!' : '✨ Story credit added!');
          if (refreshUser) await refreshUser();
          await fetchWeeklyLimit();
        }
      } catch (error) {
        console.error('Error checking single story PayPal status:', error);
        toast.error(isGerman ? 'Fehler beim Überprüfen der Zahlung' : 'Error checking payment');
      }
      window.history.replaceState({}, '', '/dashboard');
      return;
    }
    
    // Handle PayPal return (might not have session_id in URL)
    if (params.get('checkout_success') === 'true' && paymentMethod === 'paypal') {
      console.log('PayPal checkout success detected');
      
      // Get subscription ID from backend if not in URL
      let actualSessionId = subscriptionId || token_param;
      
      if (!actualSessionId) {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(
            `${API}/subscription/paypal-session`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          actualSessionId = response.data.subscription_id;
          console.log('Retrieved PayPal subscription ID from backend:', actualSessionId);
        } catch (error) {
          console.error('Error fetching PayPal session:', error);
          toast.error(t('toasts.paymentVerificationFailed'));
          window.history.replaceState({}, '', '/dashboard');
          return;
        }
      }
      
      if (actualSessionId) {
        // Poll the checkout status
        let attempts = 0;
        const maxAttempts = 10;
        
        const checkStatus = async () => {
          try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
              `${API}/subscription/checkout-status/${actualSessionId}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            console.log('PayPal checkout status response:', response.data);
            
            if (response.data.payment_status === 'paid') {
              toast.success(t('toasts.welcomeToPro'));
              
              if (refreshUser) {
                const updatedUser = await refreshUser();
                if (updatedUser) {
                  await fetchWeeklyLimit();
                  await fetchSubscriptionInfo();
                  await fetchStories();
                }
              }
              
              window.history.replaceState({}, '', '/dashboard');
              return true;
            }
            return false;
          } catch (error) {
            console.error('Error checking PayPal status:', error);
            return false;
          }
        };
        
        const success = await checkStatus();
        if (!success) {
          const interval = setInterval(async () => {
            attempts++;
            if (attempts >= maxAttempts) {
              clearInterval(interval);
              toast.error(t('toasts.paymentVerificationDelay'));
              window.history.replaceState({}, '', '/dashboard');
              return;
            }
            
            const paid = await checkStatus();
            if (paid) {
              clearInterval(interval);
            }
          }, 2000);
        }
      }
      return;
    }
    
    // Handle Stripe return (has session_id in URL)
    if (params.get('checkout_success') === 'true' && sessionId) {
      console.log('Stripe checkout success detected, checking payment status for session:', sessionId);
      
      // Poll the checkout status to trigger the upgrade
      let attempts = 0;
      const maxAttempts = 10;
      
      const checkStatus = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(
            `${API}/subscription/checkout-status/${sessionId}`,
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );
          
          console.log('Checkout status response:', response.data);
          
          if (response.data.payment_status === 'paid') {
            toast.success(t('toasts.welcomeToPro'));
            
            // Refresh user data
            if (refreshUser) {
              const updatedUser = await refreshUser();
              if (updatedUser) {
                // Refresh all data after user is updated
                await fetchWeeklyLimit();
                await fetchSubscriptionInfo();
                await fetchStories();
              }
            }
            
            // Clean up URL
            window.history.replaceState({}, '', '/dashboard');
            return true;
          }
          return false;
        } catch (error) {
          console.error('Error checking checkout status:', error);
          return false;
        }
      };
      
      // Initial check
      const success = await checkStatus();
      if (!success) {
        // If not paid yet, poll every 2 seconds
        const interval = setInterval(async () => {
          attempts++;
          if (attempts >= maxAttempts) {
            clearInterval(interval);
            toast.error(t('toasts.paymentVerificationDelay'));
            window.history.replaceState({}, '', '/dashboard');
            return;
          }
          
          const success = await checkStatus();
          if (success) {
            clearInterval(interval);
          }
        }, 2000);
      }
    }
  };

  const fetchStories = async (page = 1, reset = false, silent = false) => {
    try {
      // Only show loading skeleton if not a silent background refresh
      if (!silent) {
        if (page === 1) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }
      }
      
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/stories`, {
        params: { page, limit: 20 },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { stories: newStories, pagination: paginationData } = response.data;
      const mergeStoriesById = (incomingStories, existingStories) => {
        const seenIds = new Set(incomingStories.map((story) => story.id));
        const preservedStories = existingStories.filter((story) => !seenIds.has(story.id));

        return [...incomingStories, ...preservedStories].sort(
          (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
        );
      };
      
      if (reset || page === 1) {
        setStories((prevStories) => {
          if (silent && page === 1 && prevStories.length > 0) {
            const mergedStories = mergeStoriesById(newStories, prevStories);
            writeCachedStories(mergedStories);
            return mergedStories;
          }
          writeCachedStories(newStories);
          return newStories;
        });
      } else {
        setStories(prev => {
          const combinedStories = [...prev, ...newStories];
          writeCachedStories(combinedStories);
          return combinedStories;
        });
      }
      
      setPagination(paginationData);
    } catch (error) {
      // Only show error toast if not silent
      if (!silent) {
        toast.error(t('toasts.failedToLoadStories'));
      }
    } finally {
      if (!silent) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  const loadMoreStories = () => {
    if (pagination.has_more && !loadingMore) {
      fetchStories(pagination.page + 1, false);
    }
  };

  const fetchWeeklyLimit = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/stories/weekly-limit`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeeklyLimit(response.data);
    } catch (error) {
      console.error('Failed to fetch weekly limit');
    }
  };

  const fetchSubscriptionInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription info');
    }
  };

  const deleteStory = async (storyId) => {
    if (!window.confirm('Are you sure you want to delete this story?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/stories/${storyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('toasts.storyDeleted'));
      fetchStories();
    } catch (error) {
      toast.error(t('toasts.failedToDeleteStory'));
    }
  };

  // ============= FOLDER MANAGEMENT =============
  
  const fetchFolders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/folders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFolders(response.data.folders || []);
    } catch (error) {
      console.error('Error fetching folders:', error);
    }
  };

  const createFolder = async (folderData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/folders`, folderData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFolders([...folders, response.data.folder]);
      toast.success(isGerman ? 'Ordner erstellt' : 'Folder created');
    } catch (error) {
      toast.error(isGerman ? 'Fehler beim Erstellen' : 'Failed to create folder');
    }
  };

  const updateFolder = async (folderId, folderData) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/folders/${folderId}`, folderData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFolders(folders.map(f => f.id === folderId ? { ...f, ...folderData } : f));
      toast.success(isGerman ? 'Ordner aktualisiert' : 'Folder updated');
    } catch (error) {
      toast.error(isGerman ? 'Fehler beim Aktualisieren' : 'Failed to update folder');
    }
  };

  const deleteFolder = async (folderId) => {
    if (!window.confirm(isGerman ? 'Ordner löschen? Geschichten werden zu "Alle Geschichten" verschoben.' : 'Delete folder? Stories will be moved to "All Stories".')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/folders/${folderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFolders(folders.filter(f => f.id !== folderId));
      toast.success(isGerman ? 'Ordner gelöscht' : 'Folder deleted');
      fetchStories(1, true);
    } catch (error) {
      toast.error(isGerman ? 'Fehler beim Löschen' : 'Failed to delete folder');
    }
  };

  // ============= STORY ACTIONS =============

  const toggleFavorite = async (storyId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/stories/toggle-favorite`, {
        story_id: storyId,
        is_favorite: !currentStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setStories(stories.map(s => 
        s.id === storyId ? { ...s, is_favorite: !currentStatus } : s
      ));
      
      toast.success(isGerman ? 
        (!currentStatus ? 'Zu Favoriten hinzugefügt' : 'Aus Favoriten entfernt') :
        (!currentStatus ? 'Added to favorites' : 'Removed from favorites')
      );
    } catch (error) {
      toast.error(isGerman ? 'Fehler' : 'Error');
    }
  };

  const toggleStorySelection = (storyId) => {
    if (selectedStories.includes(storyId)) {
      setSelectedStories(selectedStories.filter(id => id !== storyId));
    } else {
      setSelectedStories([...selectedStories, storyId]);
    }
  };

  const selectAllStories = () => {
    const visibleStoryIds = filteredStories
      .filter(s => s.status !== 'generating')
      .map(s => s.id);
    setSelectedStories(visibleStoryIds);
  };

  const deselectAllStories = () => {
    setSelectedStories([]);
    setSelectionMode(false);
  };

  // ============= BULK ACTIONS =============

  const bulkMove = async (folderId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/stories/bulk-move`, {
        story_ids: selectedStories,
        folder_id: folderId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(isGerman ? 
        `${selectedStories.length} Geschichten verschoben` :
        `${selectedStories.length} stories moved`
      );
      
      deselectAllStories();
      fetchStories(1, true);
    } catch (error) {
      toast.error(isGerman ? 'Fehler beim Verschieben' : 'Failed to move stories');
    }
  };

  const bulkDelete = async () => {
    if (!window.confirm(isGerman ? 
      `${selectedStories.length} Geschichten wirklich löschen?` :
      `Really delete ${selectedStories.length} stories?`
    )) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/stories/bulk-delete`, {
        story_ids: selectedStories
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(isGerman ? 
        `${selectedStories.length} Geschichten gelöscht` :
        `${selectedStories.length} stories deleted`
      );
      
      deselectAllStories();
      fetchStories(1, true);
    } catch (error) {
      toast.error(isGerman ? 'Fehler beim Löschen' : 'Failed to delete stories');
    }
  };

  const bulkDownload = async (format) => {
    toast.info(isGerman ? 'Download wird vorbereitet...' : 'Preparing download...');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/stories/bulk-download`, {
        story_ids: selectedStories,
        format: format
      }, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', format === 'zip' ? 'StoryTales_Stories.zip' : 'StoryTales_Combined.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(isGerman ? 'Download gestartet' : 'Download started');
    } catch (error) {
      toast.error(isGerman ? 'Download fehlgeschlagen' : 'Download failed');
    }
  };

  // Combine generating stories with real stories, then filter
  const dedupedGeneratingStories = generatingStories.filter(
    (genStory) => !stories.some((story) => story.id === genStory.id)
  );
  const allStories = [...dedupedGeneratingStories, ...stories];
  
  // Filter by folder
  let folderFilteredStories = allStories;
  if (selectedFolder === 'favorites') {
    folderFilteredStories = allStories.filter(story => story.is_favorite);
  } else if (selectedFolder !== null) {
    folderFilteredStories = allStories.filter(story => story.folder_id === selectedFolder);
  }
  
  // Then filter by search term
  const filteredStories = folderFilteredStories.filter(story =>
    story.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    story.child_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    story.theme.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Calculate story counts for folders
  const storyCounts = {
    all: stories.length,
    ...folders.reduce((acc, folder) => {
      acc[folder.id] = stories.filter(s => s.folder_id === folder.id).length;
      return acc;
    }, {})
  };
  const favoriteCount = stories.filter(s => s.is_favorite).length;
  
  // Remove generating stories once a real story with similar properties appears
  useEffect(() => {
    if (generatingStories.length > 0 && stories.length > 0) {
      const newGeneratingStories = generatingStories.filter(genStory => {
        // Check if a real story exists that matches this generating story
        const realStoryExists = stories.some(story =>
          story.id === genStory.id ||
          (
            story.child_name === genStory.child_name &&
            story.theme === genStory.theme &&
            new Date(story.created_at).getTime() > Date.now() - 300000
          )
        );
        return !realStoryExists;
      });
      
      if (newGeneratingStories.length !== generatingStories.length) {
        setGeneratingStories(newGeneratingStories);
      }
    }
  }, [stories, generatingStories]);

  const formatTimeUntilReset = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const usageLabel = weeklyLimit?.period_type === 'lifetime'
    ? (isGerman ? 'Insgesamt verfügbar' : 'Available total')
    : weeklyLimit?.period_type === 'monthly'
      ? (isGerman ? 'Diesen Monat' : 'This month')
      : weeklyLimit?.period_type === 'admin'
        ? (isGerman ? 'Administratorkonto' : 'Administrator account')
        : (isGerman ? 'Verfügbar' : 'Available');

  const limitSummary = weeklyLimit?.has_limit
    ? `${weeklyLimit.used} ${isGerman ? 'von' : 'of'} ${weeklyLimit.display_limit ?? weeklyLimit.base_limit ?? weeklyLimit.limit}`
    : (isGerman ? 'Kein aktives Limit' : 'No active limit');

  const availableStoriesValue = (() => {
    if (weeklyLimit?.has_limit && typeof weeklyLimit?.remaining === 'number') {
      return weeklyLimit.remaining;
    }

    if (subscriptionInfo?.subscription_tier === 'pro') {
      const baseLimit = typeof weeklyLimit?.base_limit === 'number' && weeklyLimit.base_limit > 0
        ? weeklyLimit.base_limit
        : 4;
      const usedStories = weeklyLimit?.used ?? 0;
      return Math.max(baseLimit - usedStories, 0);
    }

    if (subscriptionInfo?.subscription_tier === 'free') {
      const baseLimit = typeof weeklyLimit?.base_limit === 'number' && weeklyLimit.base_limit > 0
        ? weeklyLimit.base_limit
        : 2;
      const usedStories = weeklyLimit?.used ?? 0;
      return Math.max(baseLimit - usedStories, 0);
    }

    return 0;
  })();

  const metricCards = [
    {
      label: isGerman ? 'Bibliothek' : 'Library',
      value: stories.length,
      hint: isGerman ? 'gespeicherte Geschichten' : 'saved stories'
    },
    {
      label: isGerman ? 'Favoriten' : 'Favorites',
      value: favoriteCount,
      hint: isGerman ? 'schnell griffbereit' : 'quick access'
    },
    {
      label: isGerman ? 'In Bearbeitung' : 'In progress',
      value: dedupedGeneratingStories.length,
      hint: isGerman ? 'werden live erstellt' : 'currently generating'
    },
    {
      label: isGerman ? 'Verfügbar' : 'Available',
      value: availableStoriesValue,
      hint: subscriptionInfo?.subscription_tier === 'pro'
        ? (
            (weeklyLimit?.bonus_stories > 0 || weeklyLimit?.purchased_unused > 0)
              ? (isGerman ? 'inkl. Bonus & Guthaben' : 'including bonus & credits')
              : (isGerman ? 'von 4 Stories pro Monat' : 'out of 4 stories per month')
          )
        : weeklyLimit?.has_limit
          ? (isGerman ? 'weitere Stories im Kontingent' : 'stories left in quota')
          : (isGerman ? 'im aktuellen Plan' : 'in current plan')
    }
  ];

  const goToSingleStoryPurchase = () => {
    navigate('/pricing');
    setTimeout(() => {
      const element = document.getElementById('single-stories');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="logo-section">
            <img 
              src="/storytales-logo.png" 
              alt="StoryTales Logo" 
              className="logo-image"
            />
          </div>
          
          {/* Desktop Navigation */}
          <div className="header-actions desktop-only">
            {weeklyLimit?.has_limit && (
              <Button
                variant="outline"
                onClick={goToSingleStoryPurchase}
                className="header-button secondary-header-button"
              >
                {isGerman ? 'Einzelne Geschichte kaufen' : 'Buy Single Story'}
              </Button>
            )}
            {subscriptionInfo && subscriptionInfo.subscription_tier === 'free' && (
              <Button
                variant="outline"
                onClick={() => navigate('/pricing')}
                className="header-button secondary-header-button"
              >
                {isGerman ? 'Auf Pro upgraden' : 'Upgrade to Pro'}
              </Button>
            )}
            <Button 
              variant="outline"
              data-testid="contact-button"
              onClick={() => navigate('/contact')}
              className="header-button"
            >
              {isGerman ? 'Kontakt' : 'Contact'}
            </Button>
            <Button 
              variant="outline"
              data-testid="settings-button"
              onClick={() => navigate('/settings')}
              className="header-button"
            >
              {isGerman ? 'Einstellungen' : 'Settings'}
            </Button>
            <Button 
              variant="outline"
              data-testid="logout-button"
              onClick={logout}
              className="header-button"
            >
              {isGerman ? 'Abmelden' : 'Logout'}
            </Button>
          </div>

          {/* Mobile Hamburger Menu */}
          <div className="mobile-menu-toggle mobile-only">
            <button 
              className="hamburger-button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <span className={`hamburger-icon ${mobileMenuOpen ? 'open' : ''}`}>
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="mobile-menu">
            {weeklyLimit?.has_limit && (
              <button 
                className="mobile-menu-item"
                onClick={() => {
                  goToSingleStoryPurchase();
                  setMobileMenuOpen(false);
                }}
              >
                {isGerman ? 'Einzelne Geschichte kaufen' : 'Buy Single Story'}
              </button>
            )}
            {subscriptionInfo && subscriptionInfo.subscription_tier === 'free' && (
              <button 
                className="mobile-menu-item"
                onClick={() => {
                  navigate('/pricing');
                  setMobileMenuOpen(false);
                }}
              >
                {isGerman ? 'Auf Pro upgraden' : 'Upgrade to Pro'}
              </button>
            )}
            <button 
              className="mobile-menu-item"
              onClick={() => {
                navigate('/contact');
                setMobileMenuOpen(false);
              }}
            >
              {isGerman ? 'Kontakt' : 'Contact'}
            </button>
            <button 
              className="mobile-menu-item"
              onClick={() => {
                navigate('/settings');
                setMobileMenuOpen(false);
              }}
            >
              {isGerman ? 'Einstellungen' : 'Settings'}
            </button>
            <button 
              className="mobile-menu-item"
              onClick={() => {
                logout();
                setMobileMenuOpen(false);
              }}
            >
              {isGerman ? 'Abmelden' : 'Logout'}
            </button>
          </div>
        )}
      </header>

      {/* Email Verification Banner */}
      {user && !user.email_verified && (
        <div className="email-verification-banner">
          <div className="verification-content">
            <span className="verification-icon">⚠️</span>
            <span className="verification-text">
              {isGerman 
                ? 'Bitte bestätige deine E-Mail-Adresse. Überprüfe deinen Posteingang auf die Bestätigungsmail.'
                : 'Please verify your email address. Check your inbox for the verification email.'}
            </span>
            <Button 
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const token = localStorage.getItem('token');
                  await axios.post(`${API}/auth/resend-verification`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  toast.success(isGerman 
                    ? 'Bestätigungsmail erneut gesendet!' 
                    : 'Verification email resent!');
                } catch (error) {
                  toast.error(isGerman 
                    ? 'Fehler beim Senden der E-Mail' 
                    : 'Error sending email');
                }
              }}
              className="resend-button"
            >
              {isGerman ? 'Erneut senden' : 'Resend'}
            </Button>
          </div>
        </div>
      )}

      <div className="dashboard-content">
        <div className="welcome-section">
          <div className="hero-shell">
            <div className="hero-copy">
              <span className="hero-eyebrow">
                {isGerman ? 'Story Studio' : 'Story Studio'}
              </span>
              <h2 data-testid="welcome-message">
                {isGerman ? `Willkommen zurück, ${user.name}!` : `Welcome back, ${user.name}!`}
              </h2>
              <p>{isGerman ? 'Erstelle magische Geschichten für die Kinder in deinem Leben' : 'Create magical stories for the children in your life'}</p>
              
              <div className="action-bar-centered">
                <Button 
                  data-testid="create-story-button"
                  className="create-button" 
                  onClick={() => navigate('/create')}
                >
                  {isGerman ? '+ Neue Geschichte erstellen' : '+ Create New Story'}
                </Button>
              </div>
            </div>

            <div className="hero-stats-panel">
              {metricCards.map((metric) => (
                <div key={metric.label} className="hero-stat-card">
                  <span className="hero-stat-label">{metric.label}</span>
                  <span className="hero-stat-value">{metric.value}</span>
                  <span className="hero-stat-hint">{metric.hint}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="dashboard-controls">
          <div className="workspace-toolbar">
            <div className="toolbar-copy">
              <span className="toolbar-kicker">{isGerman ? 'Workspace' : 'Workspace'}</span>
              <h3>{isGerman ? 'Bibliothek durchsuchen' : 'Browse your library'}</h3>
            </div>
            <div className="search-bar">
              <Input
                data-testid="search-stories-input"
                type="text"
                placeholder={isGerman ? 'Geschichten nach Titel, Kindername oder Thema suchen...' : 'Search stories by title, child name, or theme...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Button
              onClick={() => setSelectionMode(!selectionMode)}
              variant="outline"
              className="selection-mode-btn"
            >
              {selectionMode ? '✕' : '☑️'} {isGerman ? 'Auswählen' : 'Select'}
            </Button>
          </div>

          <FolderSidebar
            embedded
            folders={folders}
            selectedFolder={selectedFolder}
            onSelectFolder={setSelectedFolder}
            onCreateFolder={createFolder}
            onUpdateFolder={updateFolder}
            onDeleteFolder={deleteFolder}
            storyCounts={storyCounts}
            favoriteCount={favoriteCount}
            isGerman={isGerman}
          />
        </div>

        <div className="dashboard-main">
          {/* Stories Content */}
          <div className="stories-content">
            {/* Bulk Action Toolbar */}
            {selectionMode && selectedStories.length > 0 && (
              <BulkActionToolbar
                selectedCount={selectedStories.length}
                onMove={bulkMove}
                onDelete={bulkDelete}
                onDownload={bulkDownload}
                onDeselectAll={deselectAllStories}
                folders={folders}
                isGerman={isGerman}
              />
            )}

            {loading && filteredStories.length === 0 ? (
              <div className="stories-grid">
                <LoadingSkeleton variant="story" count={6} />
              </div>
            ) : filteredStories.length === 0 ? (
              <div className="empty-state" data-testid="empty-stories">
                <div className="empty-icon">📖</div>
                <h3>{isGerman ? 'Noch keine Geschichten' : 'No stories yet'}</h3>
                <p>{isGerman ? 'Erstelle deine erste magische Geschichte!' : 'Create your first magical story!'}</p>
              </div>
            ) : (
              <>
                {selectionMode && (
                  <div className="selection-controls">
                    <Button onClick={selectAllStories} size="sm" variant="outline">
                      {isGerman ? 'Alle auswählen' : 'Select all'}
                    </Button>
                  </div>
                )}
                
                <div className="stories-grid">
              {filteredStories.map((story) => (
                <div key={story.id} className={`story-card ${story.status === 'generating' ? 'generating' : ''} ${selectedStories.includes(story.id) ? 'selected' : ''}`} data-testid={`story-card-${story.id}`}>
                  {/* Selection Checkbox */}
                  {selectionMode && story.status !== 'generating' && (
                    <div className="story-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedStories.includes(story.id)}
                        onChange={() => toggleStorySelection(story.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  )}
                  
                  {/* Favorite Star */}
                  {story.status !== 'generating' && (
                    <button
                      className={`story-favorite ${story.is_favorite ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(story.id, story.is_favorite);
                      }}
                      title={isGerman ? (story.is_favorite ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen') : (story.is_favorite ? 'Remove from favorites' : 'Add to favorites')}
                    >
                      {story.is_favorite ? '⭐' : '☆'}
                    </button>
                  )}
                  
                  <div className="story-cover">
                    {story.status === 'generating' ? (
                      <div className="generating-cover">
                        <div className="generating-spinner">✨</div>
                        <div className="generating-text">
                          {isGerman ? 'Erstellt...' : 'Creating...'}
                        </div>
                        <div className="progress-bar">
                          <div className="progress-fill"></div>
                        </div>
                      </div>
                    ) : story.cover_image_url ? (
                      <img 
                        src={story.cover_image_url}
                        alt={story.title}
                        loading="lazy"
                        style={{ objectFit: 'cover' }}
                      />
                    ) : story.cover_image_base64 ? (
                      <img 
                        src={`data:image/png;base64,${story.cover_image_base64}`} 
                        alt={story.title}
                        loading="lazy"
                        style={{ objectFit: 'cover' }}
                      />
                    ) : (
                      <div className="placeholder-cover">📚</div>
                    )}
                  </div>
                  <div className="story-info">
                    <h3 data-testid={`story-title-${story.id}`}>{story.title}</h3>
                    <p className="story-meta">
                      {isGerman ? 'Für' : 'For'} {story.child_name}, {isGerman ? 'Alter' : 'age'} {story.age} {story.language === 'de' ? '🇩🇪' : '🇬🇧'}
                      {story.is_interactive && (
                        <span className="interactive-badge" title={isGerman ? 'Interaktive Geschichte' : 'Interactive Story'}>
                          🎮
                        </span>
                      )}
                      {story.children && story.children.length > 1 && (
                        <span className="multi-child-badge" title={isGerman ? 'Mehrere Kinder' : 'Multiple Children'}>
                          👨‍👩‍👧‍👦
                        </span>
                      )}
                    </p>
                    <p className="story-theme">{getTruncatedTheme(story)}</p>
                    <p className="story-date">
                      {new Date(story.created_at).toLocaleDateString(isGerman ? 'de-DE' : 'en-US')}
                    </p>
                  </div>
                  <div className="story-actions">
                    {story.status === 'generating' ? (
                      (() => {
                        // Check if story is stuck (older than 10 minutes)
                        const createdAt = new Date(story.created_at);
                        const ageMinutes = (Date.now() - createdAt.getTime()) / 1000 / 60;
                        const isStuck = ageMinutes > 10;
                        
                        if (isStuck) {
                          return (
                            <div className="stuck-story-info">
                              <span className="stuck-badge">
                                ⚠️ {isGerman ? 'Fehlgeschlagen' : 'Failed'}
                              </span>
                              <p className="stuck-note">
                                {isGerman 
                                  ? 'Die Erstellung hat zu lange gedauert.' 
                                  : 'Generation timed out.'}
                              </p>
                              <Button 
                                onClick={async () => {
                                  try {
                                    await axios.post(`${API}/stories/${story.id}/retry`, {}, {
                                      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                    });
                                    toast.success(isGerman ? 'Geschichte gelöscht. Du kannst eine neue erstellen.' : 'Story deleted. You can create a new one.');
                                    fetchStories();
                                  } catch (error) {
                                    toast.error(isGerman ? 'Fehler beim Löschen' : 'Failed to delete');
                                  }
                                }}
                                className="retry-button"
                                size="sm"
                              >
                                🗑️ {isGerman ? 'Löschen & Neu versuchen' : 'Delete & Retry'}
                              </Button>
                            </div>
                          );
                        }
                        
                        return (
                          <div className="generating-info">
                            <span className="generating-badge">
                              ⏳ {isGerman ? 'In Bearbeitung' : 'In Progress'}
                            </span>
                            <p className="generating-note">
                              {isGerman 
                                ? '1-2 Minuten - Seite wird automatisch aktualisiert' 
                                : '1-2 minutes - Page will auto-refresh'}
                            </p>
                            <Button 
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (window.confirm(isGerman 
                                  ? 'Möchtest du die Erstellung wirklich abbrechen?' 
                                  : 'Are you sure you want to cancel story generation?')) {
                                  try {
                                    await axios.post(`${API}/stories/${story.id}/abort`, {}, {
                                      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                    });
                                    toast.success(isGerman ? 'Erstellung abgebrochen' : 'Generation cancelled');
                                    fetchStories();
                                    // Also remove from generatingStories if it's there
                                    setGeneratingStories(prev => prev.filter(s => s.id !== story.id));
                                  } catch (error) {
                                    toast.error(error.response?.data?.detail || (isGerman ? 'Fehler beim Abbrechen' : 'Failed to cancel'));
                                  }
                                }
                              }}
                              variant="outline"
                              size="sm"
                              className="abort-button"
                              data-testid={`abort-story-${story.id}`}
                            >
                              ✕ {isGerman ? 'Abbrechen' : 'Cancel'}
                            </Button>
                          </div>
                        );
                      })()
                    ) : story.status === 'failed' ? (
                      <div className="failed-story-info">
                        <span className="failed-badge">
                          ❌ {isGerman ? 'Fehlgeschlagen' : 'Failed'}
                        </span>
                        <p className="failed-note">
                          {story.error_message || (isGerman ? 'Erstellung fehlgeschlagen' : 'Generation failed')}
                        </p>
                        <Button 
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/stories/${story.id}/retry`, {}, {
                                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                              });
                              toast.success(isGerman ? 'Geschichte gelöscht. Du kannst eine neue erstellen.' : 'Story deleted. You can create a new one.');
                              fetchStories();
                            } catch (error) {
                              toast.error(isGerman ? 'Fehler beim Löschen' : 'Failed to delete');
                            }
                          }}
                          className="retry-button"
                          size="sm"
                        >
                          🗑️ {isGerman ? 'Löschen & Neu versuchen' : 'Delete & Retry'}
                        </Button>
                      </div>
                    ) : story.status === 'aborted' ? (
                      <div className="aborted-story-info">
                        <span className="aborted-badge">
                          🛑 {isGerman ? 'Abgebrochen' : 'Cancelled'}
                        </span>
                        <p className="aborted-note">
                          {isGerman ? 'Erstellung wurde abgebrochen' : 'Generation was cancelled'}
                        </p>
                        <Button 
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/stories/${story.id}/retry`, {}, {
                                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                              });
                              toast.success(isGerman ? 'Geschichte gelöscht' : 'Story deleted');
                              fetchStories();
                            } catch (error) {
                              toast.error(isGerman ? 'Fehler beim Löschen' : 'Failed to delete');
                            }
                          }}
                          className="retry-button"
                          size="sm"
                        >
                          🗑️ {isGerman ? 'Löschen' : 'Delete'}
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Button 
                          data-testid={`read-story-button-${story.id}`}
                          onClick={() => navigate(`/story/${story.id}`)}
                          className="read-button"
                          data-icon="📖"
                        >
                          <span className="button-emoji">📖</span>
                          <span className="button-text">{isGerman ? 'Lesen' : 'Read'}</span>
                        </Button>
                        <Button 
                          onClick={() => {
                            setSelectedStoryToShare(story);
                            setShareModalOpen(true);
                          }}
                          variant="outline"
                          className="share-button"
                          data-icon="🔗"
                        >
                          <span className="button-emoji">🔗</span>
                          <span className="button-text">{isGerman ? 'Teilen' : 'Share'}</span>
                        </Button>
                        <Button 
                          data-testid={`delete-story-button-${story.id}`}
                          onClick={() => deleteStory(story.id)}
                          className="delete-button"
                          data-icon="🗑️"
                        >
                          <span className="button-emoji">🗑️</span>
                          <span className="button-text">{isGerman ? 'Löschen' : 'Delete'}</span>
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {pagination.has_more && !loading && (
              <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                <Button 
                  onClick={loadMoreStories}
                  disabled={loadingMore}
                  className="load-more-button"
                >
                  {loadingMore ? (isGerman ? 'Lädt...' : 'Loading...') : (isGerman ? 'Mehr laden' : 'Load More')}
                </Button>
              </div>
            )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Share Story Modal */}
      <ShareStoryModal 
        story={selectedStoryToShare}
        isOpen={shareModalOpen}
        onClose={() => {
          setShareModalOpen(false);
          setSelectedStoryToShare(null);
        }}
        language={user?.language || 'de'}
      />

      {/* Footer */}
      <Footer language={user?.language || 'de'} />
    </div>
  );
}
